import "./ThemeMetadataStrip.css";

import type { ThemeDetailResponse } from "@/types/themes";

import { formatRelative } from "./formatRelative";
import { StaleChip } from "./StaleChip";

export interface ThemeMetadataStripProps {
  readonly theme: ThemeDetailResponse;
  readonly categoryName?: string;
}

function formatAbsolute(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toUTCString();
}

function statusClassName(status: string): string {
  const normalised = status.toUpperCase();
  if (normalised === "ACTIVE")
    return "theme-metadata-strip__status theme-metadata-strip__status--active";
  if (normalised === "ARCHIVED")
    return "theme-metadata-strip__status theme-metadata-strip__status--archived";
  return "theme-metadata-strip__status theme-metadata-strip__status--draft";
}

/**
 * CR-111 — header strip on the theme-detail page.
 *
 * Renders the full identity + preview metadata row below the page <h1>.
 * Each field is a <dt>/<dd> pair so assistive tech can announce labels.
 * Layout is grid-based on desktop (4 columns) and stacks on narrow
 * viewports.
 */
export function ThemeMetadataStrip({
  theme,
  categoryName,
}: ThemeMetadataStripProps): JSX.Element {
  const relative = formatRelative(theme.preview_last_generated);
  const absolute = formatAbsolute(theme.preview_last_generated);
  const neverGenerated = theme.preview_last_generated === null;

  return (
    <header className="theme-metadata-strip">
      <div className="theme-metadata-strip__heading">
        <h1 className="theme-metadata-strip__title">{theme.name}</h1>
        <span className={statusClassName(theme.status)}>{theme.status}</span>
        <StaleChip active={theme.is_stale} />
      </div>
      <dl className="theme-metadata-strip__grid">
        <div className="theme-metadata-strip__item">
          <dt>ID</dt>
          <dd>
            <code className="theme-metadata-strip__mono" title={theme.id}>
              {theme.id}
            </code>
          </dd>
        </div>
        <div className="theme-metadata-strip__item">
          <dt>Slug</dt>
          <dd>
            <code className="theme-metadata-strip__mono">{theme.slug}</code>
          </dd>
        </div>
        <div className="theme-metadata-strip__item">
          <dt>Category</dt>
          <dd>{categoryName ?? theme.category_id}</dd>
        </div>
        <div className="theme-metadata-strip__item">
          <dt>Display order</dt>
          <dd>{theme.display_order}</dd>
        </div>
        <div className="theme-metadata-strip__item">
          <dt>Demo tenant</dt>
          <dd>
            {theme.preview_tenant_code ? (
              <code className="theme-metadata-strip__mono">
                {theme.preview_tenant_code}
              </code>
            ) : (
              <span className="theme-metadata-strip__muted">None</span>
            )}
          </dd>
        </div>
        <div className="theme-metadata-strip__item">
          <dt>Last generated</dt>
          <dd>
            {neverGenerated ? (
              <span className="theme-metadata-strip__muted">
                Never generated
              </span>
            ) : (
              <>
                <span>{relative}</span>
                {absolute ? (
                  <time
                    className="theme-metadata-strip__absolute"
                    dateTime={theme.preview_last_generated ?? undefined}
                  >
                    {absolute}
                  </time>
                ) : null}
              </>
            )}
          </dd>
        </div>
        <div className="theme-metadata-strip__item">
          <dt>Image count</dt>
          <dd>{theme.preview_image_count}</dd>
        </div>
      </dl>
    </header>
  );
}
